import React, { useEffect, useMemo, useRef, useState } from 'react';
import { advocateApi, freshKey } from './advocateApi';
import type { UmedFormView, UmedSignResult, UmedProposal } from './advocateApi';
import './umedSignPanel.css';

const dollars = (cents: number): string => {
  const whole = Math.abs(cents) % 100 === 0;
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 })}`;
};
const day = (iso: string): string => (iso || '').slice(0, 10);

/**
 * "Generate & sign UMED": a docked panel (not an in-table dropdown) that shows the values the form will
 * carry, collects the surrogate-authority attestation, and e-signs on the resident's behalf. One
 * Idempotency-Key per opening of the panel; the server replays a repeat of the same key.
 */
export default function UmedSignPanel({ caseId, proposal, onClose, onSigned }: {
  caseId: string;
  /** The units (and custom amount) chosen in Prepare submittal; null until one is chosen. */
  proposal: UmedProposal | null;
  onClose: () => void;
  /** The case view reloads documents and readiness. */
  onSigned: (msg: string) => void | Promise<void>;
}) {
  const [view, setView] = useState<UmedFormView | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [grantedOn, setGrantedOn] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [resign, setResign] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<UmedSignResult | null>(null);
  const [ear, setEar] = useState<'RIGHT' | 'LEFT' | ''>('');
  const key = useRef<string>(freshKey());
  const proposalKey = JSON.stringify(proposal);

  useEffect(() => {
    // The old review is cleared first: the panel is never signable against values for another proposal or ear.
    setView(null); setLoadErr(null);
    if (!proposal) return undefined;
    let live = true;
    (async () => {
      const r = await advocateApi.umedForm(caseId, proposal, ear);
      if (!live) return;
      if (r.success && r.data) {
        setView(r.data); setLoadErr(null);
        const p = r.data.attestation?.prefill;
        if (p) { setName((cur) => cur || p.responsiblePartyName || ''); setRelationship((cur) => cur || p.relationship || ''); setGrantedOn((cur) => cur || day(p.grantedOn || '')); }
      } else setLoadErr(r.message || r.error || 'The UMED could not be loaded.');
    })();
    return () => { live = false; };
  }, [caseId, proposalKey, ear]); // eslint-disable-line react-hooks/exhaustive-deps

  const missing = view?.missing || [];
  const complete = Boolean(name.trim() && relationship.trim() && grantedOn);
  const needsEar = view?.values.earCount === 1;
  const canSign = Boolean(proposal) && Boolean(view) && missing.length === 0 && complete && confirmed && (!needsEar || Boolean(ear)) && (!view?.signed || resign) && !busy;

  const sign = async () => {
    if (!canSign || !proposal) return;
    setBusy(true); setErr(null);
    const r = await advocateApi.signUmed(caseId, {
      ...proposal,
      ...(needsEar && ear ? { ear } : {}),
      responsible_party_name: name.trim(), relationship: relationship.trim(), granted_on: grantedOn, confirmed: true,
      ...(view?.signed && resign ? { resign: true } : {}),
    }, key.current);
    setBusy(false);
    if (r.success && r.data) {
      setDone(r.data);
      await onSigned('Signed UMED stored.');
    } else setErr(r.message || r.error || 'That did not go through.');
  };

  const v = view?.values;
  const items = useMemo(() => v?.lineItems || [], [v]);

  return (
    <aside className="pa-umed-panel" role="complementary" aria-label="Generate and sign UMED">
      <header className="pa-umed-bar">
        <strong>Generate &amp; sign UMED</strong>
        <button type="button" onClick={onClose} aria-label="Close the UMED panel">✕</button>
      </header>
      <div className="pa-umed-body">
        {!proposal && <p className="pa-text none">Choose the units sold in Prepare submittal first.</p>}
        {proposal && !view && !loadErr && <p className="pa-text none">Loading the UMED…</p>}
        {proposal && loadErr && <p className="pa-err" role="alert">{loadErr}</p>}
        {proposal && v && (
          <>
            <dl className="pa-umed-values">
              <dt>Name</dt><dd>{v.name}</dd>
              <dt>Case number</dt><dd>{v.caseNumber}</dd>
              <dt>Address</dt><dd>{v.address}</dd>
              <dt>City</dt><dd>{v.city}</dd>
              <dt>State</dt><dd>{v.state}</dd>
              <dt>Zip</dt><dd>{v.zip}</dd>
              <dt>Facility</dt><dd>{v.facility}</dd>
              <dt>Date</dt><dd>{v.date}</dd>
              <dt>Product</dt><dd>{v.productLabel}</dd>
              {v.liabilityCents != null && <><dt>Patient liability</dt><dd>{dollars(v.liabilityCents)}</dd></>}
              {v.termText && <><dt>Term</dt><dd>{v.termText}</dd></>}
            </dl>
            <table className="pa-umed-lines">
              <caption>Line items</caption>
              <thead><tr><th scope="col">Item</th><th scope="col">Amount</th></tr></thead>
              <tbody>
                {items.map((li, i) => <tr key={`${li.label}:${i}`}><td>{li.label}{li.ear ? ` (${li.ear === 'RIGHT' ? 'Right' : 'Left'} ear)` : ''}{li.brand || li.model ? ` ${[li.brand, li.model].filter(Boolean).join(' ')}` : ''}</td><td>{li.amountCents == null ? '' : dollars(li.amountCents)}</td></tr>)}
              </tbody>
              <tfoot><tr><th scope="row">Total</th><td>{dollars(v.totalCents)}</td></tr></tfoot>
            </table>
            {missing.length > 0 && (
              <div className="pa-umed-missing" role="alert">
                <p>This UMED cannot be signed yet. Still missing:</p>
                <ul>{missing.map((m) => <li key={m}>{m}</li>)}</ul>
              </div>
            )}
            {needsEar && (
              <fieldset className="pa-umed-attest">
                <legend>Which ear</legend>
                <label className="pa-umed-confirm"><input type="radio" name="umed-ear" checked={ear === 'RIGHT'} onChange={() => setEar('RIGHT')} /><span>Right</span></label>
                <label className="pa-umed-confirm"><input type="radio" name="umed-ear" checked={ear === 'LEFT'} onChange={() => setEar('LEFT')} /><span>Left</span></label>
              </fieldset>
            )}
            <fieldset className="pa-umed-attest">
              <legend>Surrogate authority</legend>
              <label>Responsible party name
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label>Relationship
                <input type="text" value={relationship} onChange={(e) => setRelationship(e.target.value)} />
              </label>
              <label>Date granted
                <input type="date" value={grantedOn} onChange={(e) => setGrantedOn(e.target.value)} />
              </label>
              <label className="pa-umed-confirm">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                <span>{`Surrogate authority to sign on the patient's behalf was granted by ${name || '____'}, ${relationship || '____'} on the vetting call on ${grantedOn || '____'}. I am e-signing this UMED on the resident's behalf under that authority.`}</span>
              </label>
            </fieldset>
            {view?.signed && <p className="pa-text">A signed UMED is already on file (signed {day(view.signed.signedAt)}).</p>}
            {view?.signed && (
              <label className="pa-umed-confirm">
                <input type="checkbox" checked={resign} onChange={(e) => setResign(e.target.checked)} />
                <span>Sign a corrected UMED (replaces the one on file; the BOM and SSD receive the corrected copy)</span>
              </label>
            )}
            <section className="pa-umed-recipients" aria-labelledby="umed-recipients-heading">
              <h4 id="umed-recipients-heading">The signed copy will be emailed to</h4>
              {(view?.contacts || []).length
                ? <ul>{(view?.contacts || []).map((c: { role: string; name?: string | null; email: string }) => (
                    <li key={`${c.role}:${c.email}`}>{`${c.role}: ${c.name ? `${c.name}, ` : ''}${c.email}`}</li>
                  ))}</ul>
                : <p className="pa-text">No BOM or SSD contact with an email is on file for this facility; nobody will be emailed.</p>}
            </section>
            {err && <p className="pa-err" role="alert">{err}</p>}
            <button type="button" className="pa-btn" disabled={!canSign} onClick={sign}>{busy ? 'Signing…' : 'Sign UMED'}</button>
          </>
        )}
        {done && (
          <div className="pa-umed-done" role="status">
            <p><strong>Signed UMED stored</strong></p>
            {done.noContact
              ? <p>No BOM/SSD contact on file for this facility.</p>
              : <ul>{(done.emailed || []).map((e, i) => (
                <li key={`${e.role}:${e.email}:${i}`}>
                  {e.status === 'FAILED' ? `Email to ${e.role} ${e.email} failed` : e.status === 'SKIPPED' ? `Skipped ${e.role} ${e.email}` : e.status === 'ALREADY_SENT' ? `Already emailed ${e.role} ${e.email}` : `Emailed ${e.role} ${e.email}`}
                </li>
              ))}</ul>}
            {done.handoff?.message && <p>{done.handoff.message}</p>}
          </div>
        )}
      </div>
    </aside>
  );
}
